import type {
	SenderPriorityUsage,
	SenderRowData,
	SenderStatus,
} from "@/api/custom_types";
import { ToolbarSwitcher } from "@/components/toolbar-switcher";
import { useApiError } from "@/hooks";
import { useFacets } from "@/hooks/useFacetFilters";
import { useTranslation } from "@/hooks/useTranslation";
import { Route } from "@/routes/_market/senders/index";
import {
	SENDERS_FACET_SOURCE,
	useGetSenderOwners,
	useGetSenders,
} from "@/services";
import { sendersTabAtom } from "@/store/sender";
import { cleanQuery, omit } from "@/utils/queryClient";
import { getTimeSinceText } from "@/utils/timestamp";
import { formatNumber } from "@/utils/tools";
import {
	type StatusTranslation,
	useSenderStatusTranslator,
} from "@/utils/translations";
import {
	Button,
	Chip,
	type ChipVariant,
	DataTable,
	Icon,
	TabItem,
	Tabs,
	TabsList,
	useDataTableRowSelection,
	useServerTableState,
} from "@st-marketplace/ui";
import { useNavigate } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import { useAtom } from "jotai";
import { type FC, useCallback, useMemo } from "react";
import CooldownCell from "./CooldownCell";
import KeepValueCell from "./KeepValueCell";
import PriorityCell from "./PriorityCell";
import SendersBulkAction from "./SendersBulkAction";
import { type FilterFormData, SendersFilterForm } from "./SendersFilterForm";
import SendersSingleAction from "./SendersSingleAction";
import SenderGroupFormCreate from "./groups/SenderGroupFormCreate";
import SenderGroupsSection from "./groups/SenderGroupsSection";

interface SendersProps {
	isAdmin?: boolean;
}

const STATUS_COLOR_TO_CHIP_VARIANT: Record<
	StatusTranslation["color"],
	ChipVariant
> = {
	success: "success",
	primary: "info",
	error: "error",
	warning: "warning",
};

const Senders: FC<SendersProps> = ({ isAdmin = false }) => {
	const { t, lang } = useTranslation();
	const { getErrorInterface } = useApiError();
	const navigate = useNavigate({ from: "/senders" });
	const search = Route.useSearch();
	const translateStatus = useSenderStatusTranslator(t);

	const [tabValue, setTabValue] = useAtom(sendersTabAtom);

	const listQuery = cleanQuery({
		page: search.page,
		page_size: search.pageSize,
		email: search.email,
		statuses:
			(search.statuses?.length ?? 0) > 0
				? (search.statuses as SenderStatus[])
				: undefined,
		group_names: search.groups?.length ? search.groups : undefined,
		user_id: (isAdmin && search.owner) || undefined,
		priority_usage: search.priority?.length
			? (search.priority as SenderPriorityUsage[])
			: undefined,
		on_snipe_cooldown: search.cooldown
			? search.cooldown === "active"
			: undefined,
	});

	const facetsFilters = omit(listQuery, ["page", "page_size"] as const);

	const filters: FilterFormData = useMemo(
		() => ({
			email: search.email ?? "",
			status: search.statuses ?? [],
			groups: search.groups ?? [],
			owner: search.owner ?? "",
			priority: search.priority ?? [],
			cooldown: search.cooldown ?? "",
		}),
		[search],
	);

	// The senders list is deep-linked: the URL owns the current page so a shared
	// or refreshed link restores the same page. The design-system hook still owns
	// the rest of the table state plus the globally persisted page size, so its
	// handlers are called through before the URL is updated.
	const table = useServerTableState();
	const pageIndex = Math.max(0, (search.page ?? 1) - 1);
	const pageSize = search.pageSize ?? table.pageSize;

	const server = useMemo(
		() => ({
			...table,
			pageIndex,
			pageSize,
			queryInput: { ...table.queryInput, page: pageIndex + 1, pageSize },
			onPageChange: (page: number) => {
				table.onPageChange(page);
				navigate({
					search: (prev) => ({ ...prev, page: Math.max(1, page) }),
					replace: true,
				});
			},
			onPageSizeChange: (size: number) => {
				table.onPageSizeChange(size);
				navigate({
					search: (prev) => ({ ...prev, page: 1, pageSize: size }),
					replace: true,
				});
			},
		}),
		[table, pageIndex, pageSize, navigate],
	);

	const handleSearch = useCallback(
		(values: FilterFormData) => {
			navigate({
				search: (prev) => ({
					...prev,
					email: values.email?.trim() ?? "",
					statuses: values.status ?? [],
					groups: values.groups ?? [],
					owner: values.owner ?? "",
					priority: values.priority ?? [],
					cooldown: values.cooldown ?? "",
					page: 1,
				}),
			});
		},
		[navigate],
	);

	const rowSelection = useDataTableRowSelection<SenderRowData>(true);

	const {
		data: senders,
		isLoading,
		isFetching,
		error,
		refetch,
	} = useGetSenders({ query: listQuery });

	const facetsCounts = useFacets({
		source: SENDERS_FACET_SOURCE,
		filters: facetsFilters,
		ownParams: {
			group_names: "group_names",
			senders_priorities: "priority_usage",
			snipe_cooldown: "on_snipe_cooldown",
		},
	});

	const { data: owners } = useGetSenderOwners(
		{ query: omit(facetsFilters, ["user_id"] as const) },
		{ enabled: isAdmin },
	);

	const navigateToSender = useCallback(
		(id: string) => {
			navigate({ to: `/senders/${id}` });
		},
		[navigate],
	);

	const columns = useMemo<ColumnDef<SenderRowData>[]>(() => {
		const baseColumns: ColumnDef<SenderRowData>[] = [
			{
				id: "ea_email",
				header: t("sender.tableEmail"),
				enableSorting: false,
				cell: ({ row }) => (
					<Button
						type="button"
						variant="text"
						color="primary"
						size="sm"
						onClick={() => navigateToSender(row.original.id)}
					>
						{row.original.ea_email}
					</Button>
				),
			},
			{
				id: "status",
				header: t("sender.tableStatus"),
				enableSorting: false,
				cell: ({ row }) => {
					const { color, text } = translateStatus(
						row.original.status as SenderStatus,
					);
					return (
						<Chip variant={STATUS_COLOR_TO_CHIP_VARIANT[color]} size="sm">
							{text}
						</Chip>
					);
				},
			},
			{
				id: "current_coin",
				header: t("sender.tableCoins"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.current_coin),
			},
			{
				id: "resolved_to_keep",
				header: t("sender.tableToKeep"),
				enableSorting: false,
				cell: ({ row }) => (
					<KeepValueCell resolved={row.original.resolved_to_keep} />
				),
			},
			{
				id: "available_coins",
				header: t("sender.tableAvailableCoins"),
				enableSorting: false,
				cell: ({ row }) => formatNumber(row.original.available_coins),
			},
			{
				id: "snipe_cooldown_until",
				header: t("sender.tableCooldown"),
				enableSorting: false,
				cell: ({ row }) => (
					<CooldownCell until={row.original.snipe_cooldown_until} />
				),
			},
			{
				id: "created_at",
				header: t("sender.tableAddedAt"),
				enableSorting: false,
				cell: ({ row }) =>
					(row.original.created_at &&
						getTimeSinceText(row.original.created_at, t, lang)) ||
					"-",
			},
			{
				id: "resolved_priority",
				header: t("sender.tablePriority"),
				enableSorting: false,
				cell: ({ row }) => (
					<PriorityCell resolved={row.original.resolved_priority} />
				),
			},
			{
				id: "group_name",
				header: t("sender.tableGroupName"),
				enableSorting: false,
				cell: ({ row }) =>
					("group_name" in row.original ? row.original.group_name : null) ??
					"-",
			},
			{
				id: "total_taken",
				header: t("sender.tableTotalTaken"),
				enableSorting: false,
				cell: ({ row }) =>
					"total_taken" in row.original
						? formatNumber(row.original.total_taken)
						: "-",
			},
			{
				id: "actions",
				header: "",
				enableSorting: false,
				cell: ({ row }) => (
					<SendersSingleAction selectedSender={row.original} />
				),
			},
		];

		if (isAdmin) {
			baseColumns.unshift({
				id: "user_id",
				header: t("sender.tableUserId"),
				enableSorting: false,
				cell: ({ row }) => (
					<Button
						type="button"
						variant="text"
						color="primary"
						size="sm"
						onClick={() => navigateToSender(row.original.id)}
					>
						{row.original.user_id}
					</Button>
				),
			});
		}

		return baseColumns;
	}, [t, translateStatus, lang, navigateToSender, isAdmin]);

	const handleTabChange = (value: string) => {
		setTabValue(value === "groups" ? 1 : 0);
	};

	return (
		<div className="flex flex-col gap-6 mb-10">
			<div className="flex flex-col-reverse items-stretch justify-between gap-y-4 md:flex-row md:items-center">
				<Tabs
					value={tabValue === 1 ? "groups" : "senders"}
					onValueChange={handleTabChange}
				>
					<TabsList>
						<TabItem value="senders" Icon={<Icon name="user-line" />}>
							{t("sender.tabSenders")}
						</TabItem>
						<TabItem value="groups" Icon={<Icon name="folder-line" />}>
							{t("sender.tabGroups")}
						</TabItem>
					</TabsList>
				</Tabs>
				<div className="border-b border-secondary-border pb-3">
					{tabValue === 0 ? (
						<Button
							type="button"
							color="primary"
							className="w-full normal-case md:w-auto"
							startIcon={<Icon name="add-line" />}
							onClick={() => {
								navigate({ to: "/senders/new" });
							}}
						>
							{t("sender.addButton")}
						</Button>
					) : (
						<SenderGroupFormCreate />
					)}
				</div>
			</div>

			{tabValue === 0 ? (
				<>
					<ToolbarSwitcher
						hasSelection={rowSelection.selectedIds.length > 0}
						filterComponent={
							<SendersFilterForm
								onSearch={handleSearch}
								defaultValues={filters}
								facetCounts={facetsCounts}
								owners={isAdmin ? owners ?? [] : undefined}
							/>
						}
						actionComponent={
							<SendersBulkAction
								items={rowSelection.selectedItems}
								itemsIDs={rowSelection.selectedIds}
								onClear={rowSelection.clear}
							/>
						}
					/>

					<DataTable
						columns={columns}
						data={senders?.results ?? []}
						totalItems={senders?.count ?? 0}
						server={server}
						isLoading={isLoading}
						isFetching={isFetching}
						error={error ? getErrorInterface(error) : null}
						onRetry={refetch}
						getRowId={(row) => row.id}
						rowSelection={rowSelection}
					/>
				</>
			) : (
				<SenderGroupsSection />
			)}
		</div>
	);
};

export default Senders;
